import { useEffect, useRef, useState } from 'react';

import { COUNTRIES, composeE164, type Country } from '@klaro7/validators';

/**
 * Phone input with a country selector. The collapsed control shows only the
 * country FLAG (not the dial code); the dropdown lists flag + country name +
 * dial code. The user types only the national number — the component composes
 * a single E.164 string ("+39XXXXXXXXXX") and reports it via onChange.
 */

const DEFAULT = COUNTRIES[0]!; // Italia

function countryByDial(dial: string): Country {
  return COUNTRIES.find((c) => c.dial === dial) ?? DEFAULT;
}

/** Split an E.164 value into the matching dial code + national digits. */
function parseE164(value: string): { dial: string; national: string } {
  const v = (value || '').trim();
  if (v.startsWith('+')) {
    // longest matching dial code wins (+1 vs +39 etc.)
    const match = [...COUNTRIES]
      .sort((a, b) => b.dial.length - a.dial.length)
      .find((c) => v.startsWith(c.dial));
    if (match) return { dial: match.dial, national: v.slice(match.dial.length).replace(/\D/g, '') };
  }
  return { dial: DEFAULT.dial, national: v.replace(/\D/g, '') };
}

/** Visual tone: `light` for /my and /dashboard cards, `dark` for the navy auth pages. */
export type PhoneFieldTone = 'light' | 'dark';

export function PhonePrefixInput({
  value,
  onChange,
  label,
  error,
  placeholder = '333 1234567',
  tone = 'light',
}: {
  value: string;
  onChange: (e164: string) => void;
  label?: string;
  error?: string;
  placeholder?: string;
  tone?: PhoneFieldTone;
}) {
  const initial = parseE164(value);
  const [dial, setDial] = useState(initial.dial);
  const [national, setNational] = useState(initial.national);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  const selected = countryByDial(dial);
  const isDark = tone === 'dark';

  // Valori storici salvati senza prefisso ("3357096826"): il campo li mostra
  // già scomposti, ma verso l'esterno resterebbero non-E.164 finché l'utente
  // non tocca la tastiera — e chi valida quel valore (il bottone "Invia
  // codice") resta disabilitato senza spiegare perché.
  useEffect(() => {
    const normalized = composeE164(initial.dial, initial.national);
    if (normalized && normalized !== value) onChange(normalized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Con ~240 paesi il menu non è più scorribile a occhio: si cerca per nome
  // ("svizz") o per prefisso ("+41"), che sono i due modi in cui la gente lo
  // ha in testa.
  const needle = query.trim().toLowerCase();
  const visible = needle
    ? COUNTRIES.filter(
        (c) => c.name.toLowerCase().includes(needle) || c.dial.includes(needle.replace(/^00/, '+')),
      )
    : COUNTRIES;

  // Close the dropdown on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function emit(nextDial: string, nextNational: string) {
    onChange(composeE164(nextDial, nextNational));
  }

  function selectCountry(c: Country) {
    setDial(c.dial);
    setOpen(false);
    setQuery('');
    emit(c.dial, national);
  }

  return (
    <div>
      {label && (
        <label
          className={`mb-1.5 block text-sm font-medium ${
            isDark ? 'text-white/70' : 'text-neutral-700 dark:text-white/70'
          }`}
        >
          {label}
        </label>
      )}
      <div className="flex gap-2">
        {/* Country selector — collapsed it shows flag AND dial code: la bandiera da
            sola costringe a indovinare, e con 222 paesi in lista è un invito a
            sbagliare paese senza accorgersene. */}
        <div ref={wrapRef} className="relative shrink-0">
          <button
            type="button"
            aria-label={`Paese: ${selected.name} (${selected.dial})`}
            aria-haspopup="listbox"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className={`flex h-10 items-center gap-1 rounded-lg border px-2.5 transition-colors focus:border-brand-teal/50 focus:outline-none focus:ring-2 focus:ring-brand-teal/40 ${
              isDark
                ? 'border-white/15 bg-white/[0.06] text-white'
                : 'border-neutral-300 bg-white text-neutral-900'
            }`}
          >
            <span className="text-lg leading-none">{selected.flag}</span>
            <span className="text-sm font-medium tabular-nums">{selected.dial}</span>
            <svg
              className={`h-4 w-4 transition-transform ${isDark ? 'text-white/40' : 'text-neutral-400'} ${open ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {open && (
            <ul
              role="listbox"
              className={`absolute left-0 z-20 mt-1 max-h-64 w-60 overflow-auto rounded-lg border py-1 shadow-lg ${
                isDark ? 'border-white/10 bg-[#1a1740]' : 'border-neutral-200 bg-white'
              }`}
            >
              <li role="presentation" className={`sticky top-0 border-b px-2 py-2 ${isDark ? 'border-white/10 bg-[#1a1740]' : 'border-neutral-200 bg-white'}`}>
                <input
                  type="text"
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Cerca paese o prefisso"
                  className={`h-8 w-full rounded-md border px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal/40 ${
                    isDark
                      ? 'border-white/15 bg-white/[0.06] text-white placeholder:text-white/30'
                      : 'border-neutral-300 bg-white text-neutral-900'
                  }`}
                />
              </li>
              {visible.map((c) => (
                <li key={c.code} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={c.dial === dial}
                    onClick={() => selectCountry(c)}
                    className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${
                      isDark ? 'hover:bg-white/10' : 'hover:bg-neutral-100'
                    } ${c.dial === dial ? (isDark ? 'bg-brand-teal/20 font-medium' : 'bg-brand-teal/5 font-medium') : ''}`}
                  >
                    <span className="text-lg leading-none">{c.flag}</span>
                    <span className={`flex-1 truncate ${isDark ? 'text-white' : 'text-neutral-900'}`}>
                      {c.name}
                    </span>
                    <span className={isDark ? 'text-white/40' : 'text-neutral-400'}>{c.dial}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <input
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          value={national}
          onChange={(e) => {
            const n = e.target.value.replace(/\D/g, '');
            setNational(n);
            emit(dial, n);
          }}
          placeholder={placeholder}
          className={`h-10 w-full rounded-lg border px-3 text-sm transition-colors focus:outline-none focus:ring-2 ${
            isDark
              ? 'bg-white/[0.06] text-white placeholder:text-white/30'
              : 'bg-white text-neutral-900'
          } ${
            error
              ? 'border-red-400 focus:border-red-400 focus:ring-red-300'
              : isDark
                ? 'border-white/15 focus:border-brand-teal/50 focus:ring-brand-teal/20'
                : 'border-neutral-300 focus:border-brand-teal/50 focus:ring-brand-teal/40'
          }`}
        />
      </div>
      {error && <p className={`mt-1 text-xs ${isDark ? 'text-red-300' : 'text-red-500'}`}>{error}</p>}
    </div>
  );
}
